import { HOST } from '../api/endpoints';

/** Same-origin avatar URL for <img> tags (no auth header required). */
export function getAvatarProxyUrl(userId: number): string {
  return `${HOST}/unprotected/api/v1/user/avatar/${userId}`;
}

/**
 * Prefer a direct avatar URL from the API when available (e.g. GCS sample photos).
 * Fall back to the same-origin proxy when only a user id is known.
 */
export function resolveAvatarDisplayUrl(
  avatarUrl: string | undefined,
  userId?: number
): string | undefined {
  if (!isDefaultOrMissingAvatar(avatarUrl)) {
    return avatarUrl;
  }
  if (userId != null && userId > 0) {
    return getAvatarProxyUrl(userId);
  }
  return undefined;
}

/** True when a loaded image URL is the backend default placeholder. */
export function isDefaultAvatarSrc(src: string | undefined): boolean {
  return isDefaultOrMissingAvatar(src);
}

/**
 * Treat URL as "no real photo" when missing or the backend default placeholder.
 * When true, UI should show initials instead of an image.
 * Also treats URLs that look like the default asset (e.g. path contains "default").
 */
export function isDefaultOrMissingAvatar(avatarUrl: string | undefined): boolean {
  if (!avatarUrl || avatarUrl.trim() === '') return true;
  const u = avatarUrl.toLowerCase();
  return u.includes('default.png') || u.includes('/default');
}

/**
 * Two uppercase initials: first letter of first name + first letter of last name.
 * Last name is required in our UX; if missing, uses first two letters of first name.
 */
export function getInitials(firstName: string | undefined, lastName: string | undefined): string {
  const first = (firstName || '').trim();
  const last = (lastName || '').trim();
  if (first && last) {
    return (first[0] + last[0]).toUpperCase();
  }
  if (first.length >= 2) return first.slice(0, 2).toUpperCase();
  if (first.length === 1) return first.toUpperCase();
  return '';
}

/** When only full name is available (e.g. "First Last"), returns two initials. */
export function getInitialsFromFullName(fullName: string | undefined): string {
  const s = (fullName || '').trim();
  if (!s) return '';
  const parts = s.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  if (parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
  return parts[0][0].toUpperCase();
}
